import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faBell, faPlus, faFire, faFlag, faUser, faCheckCircle, faHome } from '@fortawesome/free-solid-svg-icons';
import Api from '../../Api';
import { toast } from 'react-toastify';
import './style.css'; 

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);

  const api = Api();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response: any = await api.dashboard.getStats();
      if (response && response.result) {
        setStats(response.result.users ? response.result : response.result.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <h5 className="mt-3 text-muted fw-bold">Loading Dashboard...</h5>
        </div>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center bg-white p-5 rounded-4 shadow-sm border border-danger">
          <h4 className="text-danger fw-bold mb-3">Failed to load statistics</h4>
          <p className="text-muted">Please try refreshing the page or check the backend server connection.</p>
        </div>
      </Container>
    );
  }

  // Data for Charts
  const userPieData = [
    { name: 'Active Users', value: stats.users.active, color: '#1cc88a' },
    { name: 'Inactive Users', value: stats.users.inactive, color: '#e74a3b' }
  ];

  const propertyPieData = [
    { name: 'Active Properties', value: stats.properties.active, color: '#20c997' },
    { name: 'Unverified Properties', value: stats.properties.total - stats.properties.active, color: '#fd7e14' }
  ];

  const barData = [
    { name: 'Users', Total: stats.users.total, Verified: stats.users.verified },
    { name: 'Properties', Total: stats.properties.total, Verified: stats.properties.verified }
  ];

  return (
    <Container fluid className="dashboard-container py-4 px-2 px-md-4">
      <div>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <div>
            <h2 className="fw-bold text-dark mb-1 d-flex align-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2 text-primary"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
              Interactive Dashboard
            </h2>
            <p className="text-muted mb-0">Live statistics and insights for Cirayedaar.</p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Badge bg="primary" className="px-3 py-2 rounded-pill shadow-sm fs-6 pulse-badge d-flex align-items-center text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
              {stats.engagement.appointmentRequests} Leads
            </Badge>
            <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm fs-6 text-white">
              System Online
            </Badge>
          </div>
        </div>

        <Row className="g-3 mb-4">
          {[
            { title: 'Total Users', value: stats.users.total, color: '#4e73df', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', trend: '+4%' },
            { title: 'Active Users', value: stats.users.active, color: '#1cc88a', icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4L12 14.01l-3-3', trend: '+2%' },
            { title: 'Verified Profiles', value: stats.users.verified, color: '#36b9cc', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8', trend: null },
            { title: 'Total Properties', value: stats.properties.total, color: '#f6c23e', icon: 'M4 2h16v20H4z M9 22v-6 M15 22v-6 M9 6h6 M9 10h6 M9 14h6 M9 18h6', trend: null },
            { title: 'Active Properties', value: stats.properties.active, color: '#20c997', icon: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10', trend: null },
            { title: 'Verified Properties', value: stats.properties.verified, color: '#e74a3b', icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4L12 14.01l-3-3', trend: null },
          ].map((item, idx) => (
            <Col lg={2} md={4} xs={6} key={idx}>
              <Card className="dashboard-card border h-100 bg-white text-dark interactive-card" style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="rounded d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: `${item.color}15`, color: item.color, flexShrink: 0 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {item.icon.split(' M').map((d, i) => <path key={i} d={i === 0 ? d : `M${d}`} />)}
                      </svg>
                    </div>
                    {item.trend && (
                      <span style={{ color: '#1cc88a', fontSize: '0.75rem', fontWeight: 'bold' }}>{item.trend}</span>
                    )}
                  </div>
                  <div className="mt-auto pt-1">
                    <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: '600', lineHeight: '1.2' }}>{item.title}</p>
                    <h2 className="fw-bold mb-0 count-up" style={{ fontSize: '1.4rem', color: '#1a202c', letterSpacing: '-0.5px' }}>{item.value}</h2>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        <Row className="g-4 mb-4">
          <Col xl={6} xs={12} className="mb-4">
            <h5 className="fw-bold text-dark mb-3" style={{ fontSize: '1.1rem' }}>User Distribution</h5>
            <Card className="border shadow-sm h-100 bg-white" style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={userPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {userPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" align="center" layout="horizontal" iconType="circle" wrapperStyle={{ paddingTop: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6} xs={12} className="mb-4">
            <h5 className="fw-bold text-dark mb-3" style={{ fontSize: '1.1rem' }}>Verification Comparison</h5>
            <Card className="border shadow-sm h-100 bg-white" style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} />
                    <YAxis axisLine={false} tickLine={false} />
                    <Tooltip cursor={{fill: 'rgba(0,0,0,0.05)'}} />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                    <Bar dataKey="Total" fill="#0f7653" radius={[4, 4, 0, 0]} barSize={40} />
                    <Bar dataKey="Verified" fill="#a0aec0" radius={[4, 4, 0, 0]} barSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mb-5">
          <Col xl={6} xs={12} className="mb-4">
            <h5 className="fw-bold text-dark mb-3" style={{ fontSize: '1.1rem' }}>Property Status</h5>
            <Card className="border shadow-sm h-100 bg-white" style={{ borderRadius: '12px', borderColor: '#e2e8f0' }}>
              <Card.Body className="d-flex flex-column justify-content-center">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={propertyPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {propertyPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center text-muted px-4 mt-2" style={{ fontSize: '0.85rem' }}>
                  All properties in your portfolio are currently verified and active.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Home;
